/**
 * Accessible cost / usage estimate disclosure.
 * Hover, keyboard focus, and tap/click open the panel. Never submits work.
 */
import React, { useEffect, useId, useRef, useState } from 'react';

export function formatEstimatePanel(estimate, { plan } = {}) {
  if (!estimate && !plan) {
    return {
      title: 'Estimate unavailable',
      lines: ['Predicted usage cannot be shown for this scope.'],
      disclaimer: 'Estimated cost. Actual usage and cost may differ.',
    };
  }
  if (plan) {
    const summary = plan.summary || {};
    const est = plan.estimate || {};
    const lines = [
      `Runnable now: ${summary.run_count || 0}`,
      `Skipped (approved/unchanged): ${summary.skip_count || 0}`,
      `Blocked / awaiting review: ${summary.blocked_count || 0}`,
    ];
    (plan.nodes || [])
      .filter((n) => n.disposition === 'run')
      .forEach((n) => {
        const e = n.estimate || {};
        lines.push(
          `${n.label}: ${e.available ? e.label_text : 'Estimate unavailable'} · ${n.operation || n.type}`
        );
      });
    (plan.nodes || [])
      .filter((n) => n.awaiting_review)
      .forEach((n) => {
        lines.push(`Awaiting review: ${n.label} — ${n.reason}`);
      });
    if (est.unknown_node_ids?.length && est.known_subtotal != null) {
      lines.push(`Known subtotal: ${est.known_subtotal} ${est.currency || ''}`.trim());
      lines.push(`Unknown costs: ${est.unknown_node_ids.length} operation(s)`);
    }
    return {
      title: est.label_text || 'Estimate unavailable',
      lines,
      disclaimer: est.disclaimer || 'Estimated cost. Actual usage and cost may differ.',
      notes: est.notes || plan.notes || '',
    };
  }
  const lines = [];
  if (estimate.operation) lines.push(`Operation: ${estimate.operation}`);
  const settings = estimate.settings || {};
  Object.entries(settings).forEach(([k, v]) => {
    if (v != null && v !== '') lines.push(`${k}: ${v}`);
  });
  if (estimate.available && estimate.estimated_cost != null) {
    lines.push(
      `${estimate.is_maximum ? 'Maximum' : 'Estimated'} cost: ${estimate.estimated_cost}${
        estimate.currency ? ` ${estimate.currency}` : ''
      }`
    );
  } else {
    lines.push(estimate.label_text || estimate.label || 'Estimate unavailable');
  }
  if (estimate.quantity != null) lines.push(`Basis: ${estimate.quantity}`);
  if (estimate.notes) lines.push(estimate.notes);
  return {
    title: estimate.available
      ? estimate.is_maximum
        ? 'Maximum cost'
        : 'Estimated cost'
      : 'Estimate unavailable',
    lines,
    disclaimer: estimate.disclaimer || 'Estimated cost. Actual usage and cost may differ.',
  };
}

export default function CostEstimateTooltip({
  estimate,
  plan,
  label = 'Predicted usage and cost',
  className = '',
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const panel = formatEstimatePanel(estimate, { plan });

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!root.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span className={`workflow-cost-tip ${className}`} ref={root}>
      <button
        type="button"
        className="workflow-cost-tip-btn"
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        title={label}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen((v) => !v);
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          if (!root.current?.contains(e.relatedTarget)) setOpen(false);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        i
      </button>
      {open ? (
        <div
          id={id}
          role="tooltip"
          className="workflow-cost-tip-panel"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <strong>{panel.title}</strong>
          <ul>
            {panel.lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {panel.notes ? <p className="workflow-cost-tip-notes">{panel.notes}</p> : null}
          <p className="workflow-cost-tip-disclaimer">{panel.disclaimer}</p>
        </div>
      ) : null}
    </span>
  );
}
